/**
 * 배차 관리 TypeScript 타입 정의
 */

import { Dayjs } from 'dayjs';

/**
 * 배차 상태 열거형
 */
export enum DispatchStatus {
  /** 대기 - 배차대기 중 */
  PENDING = 'PENDING',
  /** 배차완료 - 기사에게 배차완료 */
  ASSIGNED = 'ASSIGNED',
  /** 운송중 - 운송 진행 중 */
  IN_TRANSIT = 'IN_TRANSIT',
  /** 배송완료 - 배송완료 */
  DELIVERED = 'DELIVERED',
  /** 취소 - 배차취소 */
  CANCELLED = 'CANCELLED',
}

/**
 * 배차 상태 옵션 (Select 컴포넌트용)
 */
export interface DispatchStatusOption {
  value: string;
  label: string;
}

/**
 * 배차 상태 코드를 데이터베이스 ID로 매핑
 * 백엔드 API가 Integer ID를 요구하므로 필요
 */
export const DISPATCH_STATUS_CODE_TO_ID: Record<string, number> = {
  [DispatchStatus.ASSIGNED]: 1, // 배차완료
  [DispatchStatus.CANCELLED]: 2, // 배차취소
};

/**
 * 배차 상태 옵션 목록
 */
export const DISPATCH_STATUS_OPTIONS: DispatchStatusOption[] = [
  { value: '', label: '전체' },
  { value: DispatchStatus.ASSIGNED, label: '배차완료' },
  { value: DispatchStatus.CANCELLED, label: '배차취소' },
];

/**
 * 배차 데이터 인터페이스
 */
export interface Dispatch {
  /** 배차 ID */
  id: number;
  /** 배차번호 */
  dispatchNo: string;
  /** BOL 번호 */
  bol?: string;
  /** 배차일자 (YYYY-MM-DD) */
  dispatchDate: string;
  /** 배차일시 (YYYY-MM-DD HH:mm) */
  dispatchDatetime?: string;
  /** 배송요청일 (YYYY-MM-DD) */
  requestedDeliveryDate?: string;
  /** 배차상태 코드 */
  status: DispatchStatus;
  /** 배차상태명 */
  statusName: string;
  /** 픽업상태 ID */
  pickupStatusId?: number;
  /** 픽업상태 코드 */
  pickupStatus?: string;
  /** 픽업상태명 */
  pickupStatusName?: string;
  /** 배송상태 ID */
  deliveryStatusId?: number;
  /** 배송상태 코드 */
  deliveryStatus?: string;
  /** 배송상태명 */
  deliveryStatusName?: string;
  /** 기사명 */
  driverName: string;
  /** 기사 ID */
  driverId?: number;
  /** 기사구분 코드 */
  driverType?: string;
  /** 기사구분명 (직원/개별/고객/팀기사) */
  driverTypeName?: string;
  /** 기사 연락처 */
  driverPhone?: string;
  /** 차량번호 */
  truckNo: string;
  /** 차량 ID */
  truckId?: number;
  /** 차량종류 */
  truckType?: string;
  /** 출발지 */
  departure: string;
  /** 도착지 */
  destination: string;
  /** 고객 담당자 */
  customerContactPerson?: string;
  /** 담당자 연락처 */
  customerContactPhone?: string;
  /** 화물종류 */
  cargoType?: string;
  /** 화물무게 (kg) */
  cargoWeight?: number;
  /** 운임 (원) */
  freight?: number;
  /** 비고 */
  remarks?: string;
  /** 취소일시 */
  dispatchCancelDatetime?: string;
  /** 생성일시 */
  createdAt: string;
  /** 수정일시 */
  updatedAt: string;
  /** 생성자 */
  createdBy?: string;
  /** 생성자명 */
  createdByName?: string;
  /** 수정자 */
  updatedBy?: string;
  /** 근무처 */
  workplaceName?: string;
}

/**
 * 검색 필터 인터페이스
 */
export interface DispatchSearchFilters {
  /** 시작일 */
  startDate: Dayjs | null;
  /** 종료일 */
  endDate: Dayjs | null;
  /** 배차상태 */
  status: string;
  /** 검색어 (배차번호, 기사명, 차량번호 등) */
  keyword: string;
}

/**
 * API 요청 파라미터 인터페이스
 */
export interface DispatchListParams {
  /** 페이지 번호 (1부터 시작) */
  page: number;
  /** 페이지당 항목 수 */
  pageSize: number;
  /** 시작일 (YYYY-MM-DD) */
  startDate?: string;
  /** 종료일 (YYYY-MM-DD) */
  endDate?: string;
  /** 배차상태 */
  status?: string;
  /** 검색어 */
  keyword?: string;
  /** 정렬 필드 */
  sortField?: string;
  /** 정렬 방향 (asc, desc) */
  sortOrder?: 'asc' | 'desc';
}

/**
 * API 응답 데이터 인터페이스
 */
export interface DispatchListResponse {
  /** 배차 목록 */
  data: Dispatch[];
  /** 전체 건수 */
  total: number;
  /** 현재 페이지 */
  page: number;
  /** 페이지 크기 */
  pageSize: number;
  /** 전체 페이지 수 */
  totalPages: number;
}

/**
 * 페이지네이션 상태 인터페이스
 */
export interface PaginationState {
  /** 현재 페이지 (1부터 시작) */
  current: number;
  /** 페이지당 항목 수 */
  pageSize: number;
}

/**
 * 페이지 크기 옵션
 */
export const PAGE_SIZE_OPTIONS = [30, 50, 100] as const;

/**
 * 페이지 크기 타입
 */
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * 배차 등록/수정 데이터 인터페이스
 */
export interface DispatchFormData {
  /** 배차일자 */
  dispatchDate: Date | null;
  /** 기사 ID */
  driverId: number | null;
  /** 차량 ID */
  truckId: number | null;
  /** 출발지 */
  departure: string;
  /** 도착지 */
  destination: string;
  /** 화물종류 */
  cargoType: string;
  /** 화물무게 (kg) */
  cargoWeight?: number | null;
  /** 운임 (원) */
  freight?: number | null;
  /** 비고 */
  remarks?: string;
}

/**
 * 배차 상세 정보 인터페이스 (추후 확장 가능)
 */
export interface DispatchDetail extends Dispatch {
  /** 기사 상세 정보 */
  driver?: {
    id: number;
    name: string;
    phone: string;
    licenseNo: string;
  };
  /** 차량 상세 정보 */
  truck?: {
    id: number;
    truckNo: string;
    type: string;
    capacity: number;
  };
  /** 배송 경로 정보 */
  route?: {
    distance: number;
    estimatedTime: number;
  };
}

/**
 * 기사 정보 인터페이스
 */
export interface Driver {
  /** 기사 ID */
  id: number;
  /** 사용자 아이디 (로그인 ID) */
  username?: string;
  /** 기사명 */
  name: string;
  /** 전화번호 */
  phone: string;
  /** 기사 구분 (직원/개별/고객) */
  driverType: string;
  /** 면허번호 */
  licenseNo?: string;
  /** 차량번호 */
  truckNumber?: string;
  /** 차량종류 */
  truckType?: string;
  /** 차량종류명 (영문) */
  truckTypeName?: string;
  /** 차량 ID */
  truckId?: number;
}

/**
 * 차량 정보 인터페이스
 */
export interface Truck {
  /** 차량 ID */
  id: number;
  /** 차량번호 */
  truckNumber: string;
  /** 차량종류 */
  truckType: string;
  /** 적재용량 (톤) */
  capacity?: number;
  /** 차량상태 */
  status?: string;
}

/**
 * 배송지 주소 정보 인터페이스
 */
export interface DeliveryAddress {
  /** 주소 ID */
  id: number;
  /** 주소명 */
  addressName: string;
  /** 상세주소 */
  detailAddress?: string;
  /** 담당자명 */
  personInCharge: string;
  /** 연락처 */
  contactNumber: string;
  /** 권역 ID */
  regionId: number;
  /** 권역명 (시/도) */
  regionName?: string;
  /** 상위 권역 ID */
  parentRegionId?: number;
  /** 상위 권역명 (시/군/구) */
  parentRegionName?: string;
}

/**
 * 배차 생성 폼 데이터 인터페이스
 */
export interface DispatchCreateFormData {
  /** 배송요청일 */
  requestedDeliveryDate: Dayjs | null;
  /** 배차일시 */
  dispatchDatetime: Dayjs | null;
  /** 기사 정보 */
  driver: Driver | null;
  /** 차량 정보 */
  truck: Truck | null;
  /** 출발지 주소 */
  originAddress: DeliveryAddress | null;
  /** 도착지 주소 */
  destinationAddress: DeliveryAddress | null;
}
